import { memo, useMemo } from 'react';
import { cn } from '@/lib/utils';
import { ILeadAvatarProps } from '@/@types/leads';

// Generate consistent background color from name
function getAvatarColor(name: string): string {
  const colors = [
    '#EF4444', // red
    '#F97316', // orange
    '#EAB308', // yellow
    '#22C55E', // green
    '#14B8A6', // teal
    '#3B82F6', // blue
    '#8B5CF6', // purple
    '#EC4899', // pink
  ];

  const safe = name ?? '';
  let hash = 0;
  for (let i = 0; i < safe.length; i++) {
    hash = safe.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
}

// Get initials from name
function getInitials(name: string): string {
  const trimmed = (name ?? '').trim();
  if (trimmed.length === 0) return '?';
  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) {
    return parts[0].substring(0, 2).toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export const LeadAvatar = memo(function LeadAvatar({
  name,
  profilePicture,
  size = 'md',
}: Omit<ILeadAvatarProps, 'platform'>) {
  const initials = useMemo(() => getInitials(name), [name]);
  const bgColor = useMemo(() => getAvatarColor(name), [name]);

  const sizeClasses = size === 'sm' ? 'w-7 h-7' : 'w-9 h-9';
  const textSize = size === 'sm' ? 'text-[10px]' : 'text-xs';

  return (
    <>
      {profilePicture ? (
        <img
          src={profilePicture}
          alt={name}
          className={cn(sizeClasses, 'rounded-full object-cover')}
        />
      ) : (
        <div
          className={cn(
            sizeClasses,
            'rounded-full flex items-center justify-center font-medium text-white',
            textSize
          )}
          style={{ backgroundColor: bgColor }}
        >
          {initials}
        </div>
      )}
    </>
  );
});
